import SessionManager from '../utilities/SessionManager';
import SequenceNumberHandler from '../utilities/SequenceNumberHandler';
import IpexMomentEnum from '../enums/IpexMomentEnum';
import TrackingIdEnum from '../enums/TrackingIdEnum';
import CookieReader from '../utilities/CookieReader';
import IEventData from '../types/IEventData';
import IMetaDataSchema from '../types/IMetaDataSchema';
import IPlanning from '../types/IPlanning';
import IModuleData from '../types/IModuleData';
import IEventSchema from '../types/IEventSchema';
import IApplicationMetadata from '../types/IApplicationMetadata';
import EntryPoint from '../utilities/EntryPoint';
import InsightsEventEnum from '../enums/InsightsEventEnum';
import MetadataManager from '../utilities/MetadataManager';

/**
 * Class for handling logic about sending events to Insights.
 */
export default class InsightsTarget {
	public moduleId: string;
	private sessionManager = new SessionManager();
	private sequenceNumberHandler = new SequenceNumberHandler();
	private cookie = new CookieReader();

	/**
	 * Sends a given set of data to Ipex Insights.
	 *
	 * @param ipexMoment The IPEX moment for the event.
	 * @param name Name of the event.
	 * @param payload Optional payload.
	 * @param eventData Event data to send.
	 */
	public async sendInsightsEvent(
		ipexMoment: IpexMomentEnum,
		name: string,
		eventData?: IEventData,
		payload?: {}
	): Promise<void> {
		const applicationMetadata = MetadataManager.getApplicationMetadata();
		const moduleMetadata = MetadataManager.getModuleMetadata(this.moduleId);
		this.checkEventSchema(name, ipexMoment, applicationMetadata);

		const eventBody = this.constructEventBody(
			ipexMoment,
			name,
			applicationMetadata,
			eventData,
			payload
		);

		this.sequenceNumberHandler.incrementSequenceNumber();

		const dev = applicationMetadata.dev || moduleMetadata?.dev;
		const domain = this.getIpexDomain(dev);
		const key = this.getIpexStreamKey(dev);
		try {
			await fetch(domain, {
				method: 'POST',
				body: JSON.stringify(eventBody),
				headers: { 'x-api-key': key, 'Content-Type': 'application/json' }
			});
		} catch (error) {
			throw new Error(`Failed to send ${name} event, ${error}`);
		}
	}

	/**
	 * Create and populate event data object according to IEventSchema.
	 *
	 * @param ipexMoment Moment for the event.
	 * @param name Name of the event.
	 * @param applicationMetadata The application metadata.
	 * @param eventData Optional eventData.
	 * @param payload Optional payload.
	 * @returns An event body, conforming to the IEventSchema type.
	 */
	private constructEventBody(
		ipexMoment: IpexMomentEnum,
		name: string,
		applicationMetadata: IApplicationMetadata,
		eventData?: IEventData,
		payload?: {}
	): IEventSchema {
		const location = window?.location;

		const eventBody = {
			event: name,
			moment: ipexMoment,
			['app_id']: applicationMetadata.applicationId,
			['session_id']: this.sessionManager.getId(TrackingIdEnum.sessionIdKey).id,
			['sequence_nbr']: this.sequenceNumberHandler.getSequenceNumber(),
			['visitor_timestamp']: Date.now(),
			['meta']: this.constructMetadata(applicationMetadata),
			page: {
				origin: location?.origin,
				pathname: location?.pathname,
				['query_param']: location?.search
			}
		};

		if (name == InsightsEventEnum.start) {
			const entryPoint = new EntryPoint().getEntryPoint();
			eventBody['meta']['entry_point'] = entryPoint;
		}

		const optionalParams = {
			['payload']: JSON.stringify(payload),
			['event_data']: eventData,
			planning: this.constructPlanningData(),
			module: this.constructModuleData()
		};
		for (const [key, value] of Object.entries(optionalParams)) {
			this.addOptionalParameter(eventBody, key, value);
		}
		return eventBody;
	}

	/**
	 * Create and populate metatadata, to be added to the object body.
	 *
	 * @param appData Application metadata.
	 * @returns An IMetaDataSchema object, with all relevant parameters set.
	 */
	private constructMetadata(appData: IApplicationMetadata): IMetaDataSchema {
		const pjson = require('../../package.json');
		const metadata: IMetaDataSchema = {
			['ipex_session_id']: this.sessionManager.getId(TrackingIdEnum.ipexIdKey).id,
			['long_term_id']: this.sessionManager.getId(TrackingIdEnum.longTermIdKey).id,
			['app_version']: appData.applicationVersion,
			['dpc_version']: pjson.version,
			platform: appData.platform,
			['country_code']: appData.countryCode,
			['language_code']: appData.languageCode,
			['is_planner']: appData.isPlanner
		};
		if (appData.abVersionName || appData.abVersionVariation) {
			metadata['ab_version'] = {
				name: appData.abVersionName || '',
				variation: appData.abVersionVariation || ''
			};
		}
		if (appData.enableGa === true) {
			let clientId: string;
			try {
				clientId = this.cookie.getGaId();
			} catch (error) {
				// eslint-disable-next-line
				console.error(error);
			}
			metadata['ga_client_id'] = clientId || '';
		}
		const optionalParams = {
			['store_id']: appData.storeId,
			kiosk: appData.kiosk,
			['kiosk_id']: appData.kioskId
		};
		for (const [key, value] of Object.entries(optionalParams)) {
			this.addOptionalParameter(metadata, key, value);
		}
		return metadata;
	}

	/**
	 * Create planning data for the event body, with the appropriate structure.
	 * Will set optional parameters only if they are present (i.e. Not undefined).
	 *
	 * @returns Planning data, as an IPlanning object.
	 */
	private constructPlanningData(): IPlanning {
		const planningId = this.sessionManager.getId(TrackingIdEnum.planningIdKey);
		if (planningId == null) {
			return undefined;
		}
		const planningData = {
			['planning_id']: planningId.id,
			['start_source']: planningId.startSource
		};
		this.addOptionalParameter(planningData, 'source_design_id', planningId.sourceDesignId);
		this.addOptionalParameter(planningData, 'source_spr', planningId.sourceSpr);

		return planningData;
	}

	/**
	 * Create module data for the event body, with the appropriate structure.
	 * Will set optional parameters only if they are present (i.e. Not undefined).
	 *
	 * @returns ModuleData Module data, as an IModuleData object.
	 */
	private constructModuleData(): IModuleData {
		const metadata = MetadataManager.getModuleMetadata(this.moduleId);

		if (!metadata) {
			return undefined;
		}

		const moduleData = {
			['module_id']: metadata.moduleId,
			['module_version']: metadata.moduleVersion
		};
		if (metadata.abVersionName || metadata.abVersionVariation) {
			moduleData['module_ab_version'] = {
				name: metadata.abVersionName || '',
				variation: metadata.abVersionVariation || ''
			};
		}
		return moduleData;
	}

	/**
	 * Helper method to add optional parameters to objects.
	 *
	 * @param obj The object to add a parameter to.
	 * @param key Thge parameter to add.
	 * @param val The value of the parameter.
	 */
	private addOptionalParameter(obj: object, key: string, val: any): void {
		if (val !== undefined) {
			obj[key] = val;
		}
	}

	/**
	 * Get the domain URL for web events depending on if development or production environment.
	 *
	 * @param dev True if using the development environment, false if production.
	 * @returns The domain URL address.
	 */
	private getIpexDomain(dev: boolean | undefined): string {
		return dev
			? 'https://dpc-qa.ipex-insights.com/event/submit'
			: 'https://dpc.ipex-insights.com/event/submit';
	}

	/**
	 * Get the stream key for events depending on if development or production.
	 *
	 * @param dev True if using the development environment, false if production.
	 * @returns The x-api-key for the IPEX stream.
	 */
	private getIpexStreamKey(dev: boolean): string {
		return dev
			? 'puIswII0bn2UxyC9IL8KI3aWnYCeIQmv2tya5v7b'
			: 'rZQMCYLyig4uqKOCiRBY78ypyNfCE9Cz7XVh7XdV';
	}

	/**
	 * Checks if the event name is valid.
	 *
	 * @param eventName The event name.
	 * @param moment The IPEX moment.
	 * @param applicationMetadata The app metadata.
	 * @throws An Error if eventName, moment, app_id or module_id are incorrect.
	 */
	private checkEventSchema(
		eventName: string,
		moment: IpexMomentEnum,
		applicationMetadata: IApplicationMetadata
	): void {
		if (typeof eventName !== 'string') {
			throw Error('Insights event name not defined. Will not send event.');
		}
		if (eventName.match(/[^A-Za-z0-9_]/g)) {
			throw Error(
				`Insights event ${eventName} contains special characters. Only alphanumerics and underscores are allowed.`
			);
		}
		if (eventName.length <= 3 || eventName.length > 40) {
			throw Error(
				`Insights event name ${eventName} too long or too short. Please keep it between 3 and 40 characters.`
			);
		}
		if (!applicationMetadata || Object.keys(applicationMetadata).length === 0) {
			throw Error(`Failed to send ${eventName} event, application metadata not found.`);
		}

		const applicationId = applicationMetadata.applicationId;
		if (applicationId.match(/[^A-Za-z0-9_äÄåÅöÖ]/g)) {
			throw Error(
				`Application id ${applicationId} contains special characters. Only alphanumerics and underscores are allowed.`
			);
		}

		const moduleId = this.moduleId || '';

		if (moduleId.match(/[^A-Za-z0-9_äÄåÅöÖ]/g)) {
			throw Error(
				`Module id ${moduleId} contains special characters. Only alphanumerics and underscores are allowed.`
			);
		}
		if (!Object.values(IpexMomentEnum).includes(moment)) {
			throw Error(
				`Failed to send ${eventName}, moment ${moment} does not exist in IpexMomentEnum.`
			);
		}
	}
}
